import { FC, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { GradientPage } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { ScheduleTrainingTaskCard, useScheduleTrainingTaskConfig } from '@frontend/schedule-training-task';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, ContentLoader, NakedButton, Text } from '@frontend/design-system';
import { IncompleteModuleTasksCompactCards } from '../../components/module-tasks-card-list';
import { ProgressBar } from '../../components/progress-bar';
import { TaskStatusCard } from '../../components/task-status-card';
import { useOnboardingDashboardConfig } from '../../hooks/use-onboarding-dashboard-config';
import { useOnboardingTasksSkip } from '../../hooks/use-onboarding-tasks-skip';
import { useOnboardingWidgetExpandCollapseState } from '../../hooks/use-onboarding-widget-expand-collapse-state';
import { OnboardingModuleTasksContext } from '../../provider/onboarding-module-tasks.provider';
import { DashboardOnboardingProgressTrackingIds } from '../../tracking-ids';
import { getModuleTaskStats, getTaskById } from '../../utils';

interface OnboardingDashboardCompactProps {
  containerStyles?: Interpolation<Theme>;
}
export const OnboardingDashboardCompact: FC<OnboardingDashboardCompactProps> = ({ containerStyles }) => {
  const { t } = useTranslation('onboardingDashboard');
  const navigate = useNavigate();
  const onboardingDashboardConfig = useOnboardingDashboardConfig();
  const {
    isCollapsed,
    toggleState: toggleCollapseState,
    isToggleInProgress,
  } = useOnboardingWidgetExpandCollapseState();

  const {
    selectedLocationId,
    showOnboardingDashboard,
    onboardingModuleTasks = [],
    enableSkipTasks,
    isReady,
    isLocationEligibleForV2Onboarding,
  } = onboardingDashboardConfig;

  const {
    showTrainingTask,
    hideTask: hideTrainingTask,
    isMutating: isHidingTrainingTask,
  } = useScheduleTrainingTaskConfig({
    locationId: selectedLocationId,
    checkEligibility: isLocationEligibleForV2Onboarding && isReady && !showOnboardingDashboard, // Only check eligibility if not showing the full dashboard
    checkTaskFromV2: true,
  });

  const { skipOnboardingTasks, isSkipInProgress } = useOnboardingTasksSkip({ locationId: selectedLocationId });

  const hasPendingScheduleTrainingTask = useMemo(() => {
    const task = getTaskById(OnboardingModulesTypes.TaskIds.Schedule1on1Training, onboardingModuleTasks);
    return task && task.status === OnboardingModulesTypes.TaskStatus.INCOMPLETE;
  }, [onboardingModuleTasks]);

  const { completed, progress, pending, total } = useMemo(() => {
    return getModuleTaskStats(onboardingModuleTasks);
  }, [onboardingModuleTasks]);

  const showSoloScheduleTrainingCard = showOnboardingDashboard
    ? pending === 1 && hasPendingScheduleTrainingTask
    : showTrainingTask;

  const handleSkipTasks = () => {
    skipOnboardingTasks();
    hasPendingScheduleTrainingTask && hideTrainingTask();
  };

  if (!(isLocationEligibleForV2Onboarding && isReady && (showOnboardingDashboard || showSoloScheduleTrainingCard))) {
    return null;
  }

  if (showSoloScheduleTrainingCard) {
    return (
      <ScheduleTrainingTaskCard.UIComponent
        locationId={selectedLocationId}
        isVisible
        onHideTask={hideTrainingTask}
        isMutating={isHidingTrainingTask}
        containerStyles={containerStyles}
      />
    );
  }

  return (
    <OnboardingModuleTasksContext.Provider value={onboardingDashboardConfig}>
      <div
        css={[styles.containerCard, containerStyles]}
        data-testid='onboarding-progress-compact'
        data-trackingid={DashboardOnboardingProgressTrackingIds.onboardingProgressCard}
      >
        <GradientPage gradientColor1={theme.colors.secondary.seaweed5} gradientColor2={theme.colors.indigo10}>
          <ContentLoader show={isSkipInProgress || isHidingTrainingTask} />
          <div css={styles.content}>
            <div css={styles.headerContainer}>
              <TaskStatusCard.Header color={theme.colors.black} showLogo headerStyles={styles.headerTitle}>
                {t('Welcome to Weave!')}
              </TaskStatusCard.Header>

              <div css={styles.progressSection}>
                <TaskStatusCard.Body.MilestoneText
                  color='subdued'
                  completed={completed}
                  total={total}
                  css={styles.milestoneText}
                  responsive
                />
                <ProgressBar
                  progress={progress}
                  barColor={theme.colors.indigo50}
                  trackColor={theme.colors.white}
                  containerStyles={styles.progressBar}
                />
                <div css={styles.actionsBar}>
                  <Button
                    variant='tertiary'
                    onClick={() => navigate({ to: '/home/onboarding' })}
                    trackingId={DashboardOnboardingProgressTrackingIds.viewAllTasks}
                    aria-label='View All Tasks'
                  >
                    <Text as='span' weight='bold' size='medium' color='light'>
                      {t('View All')}
                    </Text>
                  </Button>

                  {enableSkipTasks && (
                    <Button
                      variant='tertiary'
                      onClick={handleSkipTasks}
                      disabled={isSkipInProgress}
                      aria-label='Skip Remaining Tasks'
                      trackingId={DashboardOnboardingProgressTrackingIds.skipRemaining}
                    >
                      <Text as='span' weight='bold' size='medium' color='light'>
                        {t('Skip Remaining')}
                      </Text>
                    </Button>
                  )}
                </div>
              </div>
              <CollapseToggleButton
                isCollapsed={isCollapsed}
                onToggle={toggleCollapseState}
                isToggleInProgress={isToggleInProgress}
              />
            </div>
            {!isCollapsed && <IncompleteModuleTasksCompactCards moduleTasks={onboardingModuleTasks} />}
          </div>
        </GradientPage>
      </div>
    </OnboardingModuleTasksContext.Provider>
  );
};

const CollapseToggleButton: FC<
  ButtonProps & { isCollapsed?: boolean; onToggle: () => void; isToggleInProgress: boolean }
> = ({ isCollapsed, onToggle, isToggleInProgress, ...props }) => {
  return (
    <NakedButton
      trackingId={
        isCollapsed
          ? DashboardOnboardingProgressTrackingIds.expandCard
          : DashboardOnboardingProgressTrackingIds.collapseCard
      }
      disabled={isToggleInProgress}
      onClick={onToggle}
      {...props}
    >
      <Icon name='caret-up-small' css={[styles.collapseToggleBtn, isCollapsed && styles.collapseIcon]} />
    </NakedButton>
  );
};

const styles = {
  containerCard: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
    padding: 0;
    box-shadow: ${theme.shadows.floating};
    position: relative;
    z-index: 0;
    overflow: hidden;
    container-type: inline-size;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
  `,

  headerContainer: css`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 'header progress collapse';
    align-items: center;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.max}px) {
      grid-template-columns: 1fr auto;
      grid-template-areas:
        'header collapse'
        'progress progress';
      gap: ${theme.spacing(1)};
    }
  `,
  headerTitle: css`
    grid-area: header;
  `,
  progressSection: css`
    grid-area: progress;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 'milestones progressBar actions';
    align-items: center;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      grid-template-columns: auto auto;
      grid-template-areas: 'milestones actions';
      gap: ${theme.spacing(1)};
    }
  `,

  milestoneText: css`
    grid-area: milestones;
  `,
  progressBar: css`
    grid-area: progressBar;
    @container (max-width: ${breakpoints.small.min}px) {
      display: none;
    }
  `,
  actionsBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.spacing(1)};
    grid-area: actions;
    @container (max-width: ${breakpoints.small.min}px) {
      gap: 0;
    }
  `,
  collapseToggleBtn: css`
    grid-area: collapse;
    transition: transform 0.3s ease-in-out;
  `,
  collapseIcon: css`
    transform: rotate(180deg);
  `,
};
